import {
  AANAccountType,
  AccountTypes,
  GrantedRole,
  ISelectedAccount,
  RegisteredRole,
} from '@interfaces/models';
import { RoleTypes } from '@interfaces/models/role-types';
import { AvgCostConversion } from '@interfaces/models/spec-id-fallback.enum';
import { InternalTransferState } from '@interfaces/state/internal-transfer/internal-transfer.interface';
import { createSelector } from '@ngrx/store';
import { parseStrDefaultToZero } from '@sharedUtils';
import { baseConfig } from '@src/environments/config';
import {
  selectMultiITFromSelectedAccount,
  selectMultiITTotalTransferAmount,
  selectMultiITTriageTransferFrom,
} from '@store/internal-transfers/selectors/multi-internal-transfer.selectors';

import * as fromFeature from '../../reducers/index';
import * as fromSelectedInternalTransferState from '../../reducers/internal-transfer/internal-transfer.reducer';
import { selectDivorceToggle, selectItMultiReceivingToggle } from '../feature/feature.selectors';
import { assertsIsCurrentAccount } from '../receipt/internal-transfer/cashier-internal-transfer';

type MultiSignerAccountData = ISelectedAccount['accountData'] | undefined;

function hasMultipleExecutors(accountData: MultiSignerAccountData): boolean {
  const registeredRoles: RegisteredRole[] = accountData?.registeredRoles ?? [];
  const grantedRoles: GrantedRole[] = accountData?.grantedRoles ?? [];

  const uniqueExecutorRoles = Array.from(
    new Map(
      [...registeredRoles, ...grantedRoles]
        .filter((role) => role.roleType === RoleTypes.EXEX || role.roleType === RoleTypes.EXAD)
        .map((role) => [role.poid, role]),
    ).values(),
  );

  return uniqueExecutorRoles.length > 1;
}

export function isMultiSignerForEstateAccount(itState: InternalTransferState): boolean {
  const isEstateFrom = itState.triage.transferFrom === AccountTypes.ESTATE;
  if (!isEstateFrom) return false;

  return hasMultipleExecutors(itState?.transferFrom?.selectedAccount?.accountData);
}

/**
 * Shared multi-signer determination for the source account, usable by both the
 * classic single-receiving flow and the multi-IT flow. A transfer requires
 * multiple signers when the source is an estate with more than one executor, or a
 * joint account above the single-signer limit.
 */
function computeIsMultiSignerScenario(
  transferFromType: AccountTypes,
  accountData: MultiSignerAccountData,
  estimatedAmount: number,
): boolean {
  const isMultiSignerForEstate =
    transferFromType === AccountTypes.ESTATE && hasMultipleExecutors(accountData);

  const aanAccountType = accountData?.accountType;
  const isMultiSignerForJoint =
    (aanAccountType === AANAccountType.JOINT_TENANTS_WROS &&
      estimatedAmount >= baseConfig.singleSignerLimitJointTenantsWROSAccount) ||
    (transferFromType === AccountTypes.JOINT &&
      aanAccountType !== AANAccountType.JOINT_TENANTS_WROS);

  return isMultiSignerForEstate || isMultiSignerForJoint;
}

export const selectInternalTransferState = createSelector(
  fromFeature.getInternalTransferState,
  (state) => state,
);

export const selectInternalTransferIsOwner = createSelector(
  selectInternalTransferState,
  selectDivorceToggle,
  (itState, divorceToggle) => {
    if (divorceToggle) {
      return !!assertsIsCurrentAccount(itState.transferTo?.selectedAccount ?? null);
    }
    return !!fromSelectedInternalTransferState.getInternalTransferIsOwner(itState);
  },
);

export const selectInternalTransferTriage = createSelector(
  selectInternalTransferState,
  selectInternalTransferIsOwner,
  (itState, isOwner) => {
    return { ...itState.triage, isOwner };
  },
);

export const selectInternalTransferFrom = createSelector(
  selectInternalTransferState,
  fromSelectedInternalTransferState.getInternalTransferFrom,
);

export const selectInternalTransferAccountFrom = createSelector(
  selectInternalTransferState,
  fromSelectedInternalTransferState.getInternalTransferAccountFrom,
);

export const selectInternalTransferFromAccountType = createSelector(
  selectInternalTransferState,
  fromSelectedInternalTransferState.getInternalTransferFromAccountType,
);

export const selectInternalTransferToAccountType = createSelector(
  selectInternalTransferState,
  fromSelectedInternalTransferState.getInternalTransferToAccountType,
);

export const selectInternalTransferTo = createSelector(
  selectInternalTransferState,
  fromSelectedInternalTransferState.getInternalTransferTo,
);

export const selectInternalTransferAccountTo = createSelector(
  selectInternalTransferState,
  fromSelectedInternalTransferState.getInternalTransferAccountTo,
);

export const selectTransferMethod = createSelector(
  selectInternalTransferState,
  fromSelectedInternalTransferState.getInternalTransferMethod,
);

export const selectTransferType = createSelector(
  selectInternalTransferState,
  fromSelectedInternalTransferState.getInternalTransferType,
);

export const selectTransferAllSelected = createSelector(
  selectInternalTransferState,
  fromSelectedInternalTransferState.getInternalTransferTransferAllSelected,
);

export const selectInternalTransferIsTransferConditionMatch = createSelector(
  selectInternalTransferState,
  fromSelectedInternalTransferState.getInternalTransferIsTransferConditionMatch,
);

export const selectInternalTransferIsDivorceTransfer = createSelector(
  selectInternalTransferState,
  fromSelectedInternalTransferState.getInternalTransferIsDivorceTransfer,
);

export const selectInternalTransferHasAvgCostHoldings = createSelector(
  selectInternalTransferState,
  fromSelectedInternalTransferState.getInternalTransferHasAvgCostHoldings,
);

export const selectInternalTransferTotalTransferValue = createSelector(
  selectInternalTransferState,
  fromSelectedInternalTransferState.getInternalTransferTotalTransferValue,
);

export const selectInternalTransferConvertSpecIDHoldings = createSelector(
  selectInternalTransferState,
  fromSelectedInternalTransferState.getInternalTransferConvertSpecIDHoldings,
);

export const selectInternalTransferConvertAvgCostHoldings = createSelector(
  selectInternalTransferState,
  (itState) => {
    const managed = itState?.transferFrom?.selectedAccount?.accountData?.managed;
    if (managed) return false;

    const convertAvgCost =
      fromSelectedInternalTransferState.getInternalTransferAvgCostConversion(itState) ===
      AvgCostConversion.FIFO;
    return convertAvgCost;
  },
);

export const selectInternalTransferTransactionType = createSelector(
  selectInternalTransferState,
  fromSelectedInternalTransferState.getInternalTransferTransactionType,
);

export const selectInternalTransferPercentageOfHoldings = createSelector(
  selectInternalTransferState,
  fromSelectedInternalTransferState.getInternalTransferPercentageOfHoldings,
);

export const selectInternalTransferAvgCostConversion = createSelector(
  selectInternalTransferState,
  fromSelectedInternalTransferState.getInternalTransferAvgCostConversion,
);

export const selectInternalTransferMinTaxConversion = createSelector(
  selectInternalTransferState,
  fromSelectedInternalTransferState.getInternalTransferMinTaxConversion,
);

export const selectIsMinorGift = createSelector(
  selectInternalTransferFromAccountType,
  selectInternalTransferToAccountType,
  (from, to) => {
    return from !== AccountTypes.ESTATE && to === AccountTypes.UGMA_UTMA;
  },
);

export const selectAnsweredNoToTriageGiftQuestion = createSelector(
  selectInternalTransferTriage,
  (triage) => {
    const possibleAccountTypesForGiftQuestion = [
      AccountTypes.INDV,
      AccountTypes.TRUST,
      AccountTypes.JOINT,
    ];
    const isNotGift =
      possibleAccountTypesForGiftQuestion.includes(triage?.transferFrom) &&
      possibleAccountTypesForGiftQuestion.includes(triage?.transferTo) &&
      triage?.isGift === false;
    return isNotGift;
  },
);

export const selectIsOrgTransfer = createSelector(
  selectInternalTransferTriage,
  (triage) => triage?.transferFrom === AccountTypes.ORG || triage?.transferTo === AccountTypes.ORG,
);

export const selectIsOrgToOrgTransfer = createSelector(
  selectInternalTransferTriage,
  (triage) => triage?.transferFrom === AccountTypes.ORG && triage?.transferTo === AccountTypes.ORG,
);

export const selectIsOrgToNonOrgTransfer = createSelector(
  selectInternalTransferTriage,
  (triage) =>
    triage?.transferFrom === AccountTypes.ORG &&
    triage?.transferTo != null &&
    triage.transferTo !== AccountTypes.ORG,
);

export const selectIsFromAccountVC = createSelector(
  selectInternalTransferState,
  (state) => state.transferFrom?.selectedAccount?.accountData?.isVanguardCharitable === true,
);

export const selectITSummaryStepsVisible = createSelector(
  selectInternalTransferTriage,
  selectIsMinorGift,
  selectInternalTransferIsDivorceTransfer,
  selectAnsweredNoToTriageGiftQuestion,
  selectInternalTransferIsTransferConditionMatch,
  selectIsOrgTransfer,
  (
    triage,
    isMinorGift,
    isDivorceTransfer,
    isNotGiftTransfer,
    isTransferConditionsAffirmed,
    isOrgTransfer,
  ) => {
    const visible =
      (triage?.exceptionsApply ||
        triage?.transferFrom === 'ESTATE' ||
        !!triage?.isOwner ||
        isDivorceTransfer ||
        isNotGiftTransfer ||
        isTransferConditionsAffirmed ||
        isOrgTransfer) &&
      triage.transferTo !== 'ESTATE' &&
      !isMinorGift;
    return !!visible;
  },
);

export const selectIsInternalTransferMultiSignerScenario = createSelector(
  selectInternalTransferState,
  selectItMultiReceivingToggle,
  selectMultiITTriageTransferFrom,
  selectMultiITFromSelectedAccount,
  selectMultiITTotalTransferAmount,
  (
    itState,
    isMultiReceiving,
    multiITTransferFrom,
    multiITFromAccount,
    multiITTotalAmount,
  ): boolean => {
    // When the multi-receiving feature is on, the multi-IT slice is the source of
    // truth for the transfer, so evaluate the multi-signer rules against it.
    if (isMultiReceiving) {
      return computeIsMultiSignerScenario(
        multiITTransferFrom,
        multiITFromAccount?.accountData,
        multiITTotalAmount,
      );
    }

    return computeIsMultiSignerScenario(
      itState.triage.transferFrom,
      itState.transferFrom?.selectedAccount?.accountData,
      parseStrDefaultToZero(itState.transferFrom?.totalTransferValue),
    );
  },
);
